import React from "react";
import type { User } from "../UsuariosPageNew";
import { getInitials } from "../UsuariosPageNew";

interface UserCardProps {
    user: User;
    onUserClick: (user: User) => void;
    onActionClick: (user: User, type: "status" | "superhost", e?: React.MouseEvent) => void;
}

const UserCard: React.FC<UserCardProps> = ({ user, onUserClick, onActionClick }) => {
    const isActive = user.status === "AC";
    
    // Si no tiene el array, hacemos fallback al string antiguo role
    const isHost = user.roles ? user.roles.includes("Host") : user.role === "Host";
    
    const isSuperHost = user.isSuperHost === true;

    return (
        <div
            onClick={() => onUserClick(user)}
            className={`bg-white border rounded-xl p-[18px] cursor-pointer transition-all duration-200 relative flex flex-col gap-3 group
                ${isSuperHost ? "border-amber-200 shadow-[0_0_0_1px_rgba(251,191,36,0.2)] hover:border-amber-400" : "border-gray-200 hover:border-[#4f6df5]"} 
                ${!isActive ? "opacity-70 hover:border-red-300" : "hover:-translate-y-0.5 hover:shadow-lg"}
            `}
        >
            {isSuperHost && (
                <div className="absolute top-3 right-3 text-[10px] font-bold bg-gradient-to-br from-amber-400 to-amber-600 text-white py-0.5 px-2 rounded-md tracking-wider shadow-sm">
                    ⭐ Super Anfitrión
                </div>
            )}

            <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full shrink-0 flex items-center justify-center text-[17px] font-bold text-white relative overflow-hidden bg-gradient-to-br from-[#4f6df5] to-[#2563eb]">
                    {user.photoLink ? (
                        <img src={user.photoLink} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                        getInitials(user.name, user.lastName)
                    )}
                    <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white z-10 ${isActive ? "bg-emerald-500" : "bg-red-500"}`}></div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900 truncate">{user.name} {user.lastName}</div>
                    <div className="text-[11px] text-gray-500 truncate mt-0.5">{user.email || "Sin correo"}</div>
                    
                    {/* Contenedor de Etiquetas (Roles y Estado) */}
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        
                        {user.roles && user.roles.length > 0 ? (
                            user.roles
                                .filter(r => r === "Host" || r === "Guest")
                                .map((rol, idx) => (
                                    <span 
                                        key={idx} 
                                        className={`text-[9px] font-bold px-[7px] py-[2px] rounded-md uppercase tracking-wider border ${rol === "Host" ? "bg-purple-50 text-purple-600 border-purple-100" : "bg-blue-50 text-blue-600 border-blue-100"}`}
                                    >
                                        {rol === "Host" ? "Anfitrión" : "Huésped"}
                                    </span>
                                ))
                        ) : (
                            // Fallback por si la API aún manda el string antiguo
                            <span className={`text-[9px] font-bold px-[7px] py-[2px] rounded-md uppercase tracking-wider border ${isHost ? "bg-purple-50 text-purple-600 border-purple-100" : "bg-blue-50 text-blue-600 border-blue-100"}`}>
                                {user.roleLabel || (isHost ? "Anfitrión" : "Huésped")}
                            </span>
                        )}
                        
                        <span className={`text-[9px] font-bold px-[7px] py-[2px] rounded-md uppercase tracking-wider border ${isActive ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-red-50 text-red-600 border-red-100"}`}>
                            {isActive ? "Activo" : "Suspendido"}
                        </span>
                    </div>
                </div>
            </div>

            <div className="h-px bg-gray-100"></div>

            <div className="flex justify-between">
                <div className="text-center">
                    <div className="font-mono text-sm font-bold text-[#4f6df5]">{user.reservas}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Reservas</div>
                </div>
                <div className="text-center">
                    <div className="font-mono text-sm font-bold text-emerald-600">${user.monto?.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{user.tipoMonto}</div>
                </div>
                <div className="text-center">
                    <div className="font-mono text-sm font-bold text-amber-500">{user.rating ? `${user.rating} ★` : '—'}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Rating</div>
                </div>
            </div>

            <div className="flex gap-1.5 mt-1">
                <button className="flex-1 py-1.5 border border-gray-200 bg-white text-gray-600 rounded-md text-[11px] font-semibold transition-colors hover:border-[#4f6df5] hover:text-[#4f6df5] hover:bg-blue-50 shadow-sm">
                    Ver perfil
                </button>
                {isHost && isActive ? (
                    <button
                        className="flex-1 py-1.5 border border-gray-200 bg-white text-gray-600 rounded-md text-[11px] font-semibold transition-colors hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50 shadow-sm"
                        onClick={(e) => onActionClick(user, "superhost", e)}
                    >
                        ⭐ Super Anf.
                    </button>
                ) : (
                    <button
                        className={`flex-1 py-1.5 border border-gray-200 bg-white text-gray-600 rounded-md text-[11px] font-semibold transition-colors shadow-sm ${isActive ? "hover:border-red-300 hover:text-red-600 hover:bg-red-50" : "hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50"}`}
                        onClick={(e) => onActionClick(user, "status", e)}
                    >
                        {isActive ? "Suspender" : "Reactivar"}
                    </button>
                )}
            </div>
        </div>
    );
};

export default UserCard;